import React, { useEffect, useMemo, useRef } from 'react';
import { Input, Label } from 'reactstrap';
import useSWR from 'swr';
import { isLangeVerzoekenWeek } from '../constants';
import { useAfasContext } from '../context/AfasContext';
import AfasTabSelect from './AfasTabSelect';

const pickLatestWeekValue = (weeks) => {
    const calendarWeeks = weeks.filter((week) => !isLangeVerzoekenWeek(week.week));
    const pool = calendarWeeks.length > 0 ? calendarWeeks : weeks;
    if (!pool.length) {
        return '';
    }

    const latest = pool.reduce((best, week) =>
        Number(week.week) > Number(best.week) ? week : best,
    );

    return String(latest.week);
};

const AfasListToolbar = ({
    showPeriod = false,
    showWeek = false,
    listMode = 'auto',
    selectedWeek = '',
    onWeekChange,
    compact = false,
}) => {
    const {
        communities,
        communityId,
        setCommunityId,
        historiePeriod,
        setHistoriePeriod,
        AfasService,
    } = useAfasContext();

    const { data: periodsResponse } = useSWR(
        showPeriod && communityId ? ['afas-historie-periods', communityId] : null,
        () => AfasService.getHistoriePeriods(communityId).then((res) => res.data),
    );

    const { data: weeksResponse } = useSWR(
        showWeek && communityId ? ['afas-openstaand-weeks', communityId, listMode] : null,
        () => AfasService.getOpenstaandWeeks(communityId, listMode).then((res) => res.data),
    );

    const periods = periodsResponse?.data?.periods ?? periodsResponse?.data ?? [];
    const weeks = weeksResponse?.data ?? [];
    const latestWeek = useMemo(() => pickLatestWeekValue(weeks), [weeks]);
    const previousCommunityIdRef = useRef(communityId);

    useEffect(() => {
        if (!showWeek || !communityId || !onWeekChange || !latestWeek) {
            return;
        }

        const communityChanged = previousCommunityIdRef.current !== communityId;
        previousCommunityIdRef.current = communityId;

        const weekValues = weeks.map((week) => String(week.week));
        const selectedIsValid =
            selectedWeek && weekValues.includes(String(selectedWeek));

        if (communityChanged || !selectedIsValid) {
            onWeekChange(latestWeek);
        }
    }, [showWeek, communityId, weeks, latestWeek, selectedWeek, onWeekChange]);

      const controlClass = compact ? 'form-select form-select-sm' : 'form-select';

    return (
        <div
            className={[
                'afas-list-toolbar d-flex align-items-center',
                compact
                    ? 'afas-list-toolbar--compact flex-nowrap gap-2 m-0 p-0 border-0 bg-transparent shadow-none'
                    : 'flex-wrap gap-2 p-3 bg-light rounded border mb-3',
            ]
                .filter(Boolean)
                .join(' ')}
            style={compact ? { background: 'transparent', padding: 0, border: 'none', boxShadow: 'none' } : undefined}
        >
            <div className="afas-list-toolbar__field" style={{ minWidth: compact ? '140px' : '200px' }}>
                {!compact && (
                    <Label className="form-label small fw-semibold text-muted mb-1">Selecteer organisatie</Label>
                )}
                <Input
                    type="select"
                    className={controlClass}
                    value={communityId ?? ''}
                    onChange={(event) => setCommunityId(Number(event.target.value))}
                    aria-label="Selecteer organisatie"
                >
                    <option value="">Selecteer organisatie</option>
                    {communities.map((community) => (
                        <option key={community.iCommunityId} value={community.iCommunityId}>
                            {community.vCommunityName ?? community.name}
                        </option>
                    ))}
                </Input>
            </div>
            <div className="afas-list-toolbar__field" style={{ minWidth: compact ? '130px' : '180px' }}>
                {!compact && (
                    <Label className="form-label small fw-semibold text-muted mb-1">Tab</Label>
                )}
                <AfasTabSelect className={controlClass} />
            </div>
            {showWeek && (
                <div className="afas-list-toolbar__field" style={{ minWidth: compact ? '140px' : '180px' }}>
                    {!compact && (
                        <Label className="form-label small fw-semibold text-muted mb-1">Selecteer week</Label>
                    )}
                    <Input
                        type="select"
                        className={controlClass}
                        value={selectedWeek || latestWeek}
                        onChange={(event) => onWeekChange?.(event.target.value)}
                        disabled={!communityId || weeks.length === 0}
                        aria-label="Selecteer week"
                    >
                        {weeks.length === 0 && (
                            <option value="">Selecteer week</option>
                        )}
                        {weeks.map((week) => (
                            <option key={week.week} value={String(week.week)}>
                                {week.label}
                                {week.count != null ? ` (${week.count})` : ''}
                            </option>
                        ))}
                    </Input>
                </div>
            )}
            {showPeriod && (
                <div className="afas-list-toolbar__field" style={{ minWidth: compact ? '140px' : '180px' }}>
                    {!compact && (
                        <Label className="form-label small fw-semibold text-muted mb-1">Selecteer periode</Label>
                    )}
                    <Input
                        type="select"
                        className={controlClass}
                        value={historiePeriod}
                        onChange={(event) => setHistoriePeriod(event.target.value)}
                        aria-label="Selecteer periode"
                    >
                        <option value="">Alle periodes</option>
                        {periods.map((period) => (
                            <option key={period.value ?? period} value={period.value ?? period}>
                                {period.label ?? period}
                            </option>
                        ))}
                    </Input>
                </div>
            )}
        </div>
    );
};

export default AfasListToolbar;
